import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import styles from './Login.module.scss';
import { ReactComponent as Logo } from '../../assets/icons/fa.svg';

const TABS = [
  { id: 'login', label: 'Войти' },
  { id: 'register', label: 'Регистрация' },
];

const MODAL_ANIM_MS = 300;

const Login = ({ trigger = 'Войти', triggerClassName = '' }) => {
  const [activeTab, setActiveTab] = useState('login');
  const [pendingTab, setPendingTab] = useState('login');
  const [isOpen, setIsOpen] = useState(false);
  const [isShown, setIsShown] = useState(false);
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });

  const [touchedFields, setTouchedFields] = useState({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState('');

  // Новое состояние: в фокусе ли сейчас поле пароля
  const [isPasswordFocused, setIsPasswordFocused] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (touchedFields[name]) {
      setTouchedFields((prev) => ({ ...prev, [name]: false }));
    }

    if (serverError) setServerError('');
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouchedFields((prev) => ({ ...prev, [name]: true }));
  };

  const isEmailValid = useMemo(() => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email);
  }, [formData.email]);

  const isNameValid = useMemo(() => {
    const name = formData.name.trim();
    return (
      /^[a-zA-Zа-яА-ЯёЁ\s]+$/.test(name) &&
      name.length >= 2 &&
      name.length <= 40
    );
  }, [formData.name]);

  // Критерии пароля для тултипа
  const passwordCriteria = useMemo(() => {
    const pass = formData.password;
    return [
      { id: 'length', label: 'От 8 символов', isValid: pass.length >= 8 },
      {
        id: 'number',
        label: 'Хотя бы одна цифра',
        isValid: /[0-9]/.test(pass),
      },
      {
        id: 'special',
        label: 'Спец. символ',
        isValid: /[^a-zA-Zа-яА-Я0-9\s]/.test(pass),
      },
    ];
  }, [formData.password]);

  const passwordStrength = useMemo(() => {
    const pass = formData.password;
    if (!pass) return 'none';
    if (pass.length < 8) return 'weak';

    let score = 0;
    if (/[a-zа-я]/i.test(pass)) score++;
    if (/[0-9]/.test(pass)) score++;
    if (/[^a-zA-Zа-яА-Я0-9\s]/.test(pass)) score++;

    if (score >= 3 && pass.length >= 10) return 'strong';
    if (score >= 2) return 'medium';
    return 'weak';
  }, [formData.password]);

  const isPassMatch =
    formData.password === formData.confirmPassword &&
    formData.confirmPassword !== '';

  const isLogin = activeTab === 'login';
  const canLogin = isEmailValid && formData.password.trim().length > 0;
  const canRegister =
    isNameValid && isEmailValid && passwordStrength !== 'weak' && isPassMatch;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isLogin && !canLogin) return;
    if (!isLogin && !canRegister) return;

    setLoading(true);
    setServerError('');

    try {
      const url = isLogin ? '/api/auth/login' : '/api/auth/register';

      const payload = isLogin
        ? {
            email: formData.email.trim().toLowerCase(),
            password: formData.password,
          }
        : {
            name: formData.name.trim(),
            email: formData.email.trim().toLowerCase(),
            password: formData.password,
          };

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => null);

      if (!res.ok) {
        setServerError(data?.error || 'Что-то пошло не так');
        return;
      }

      closeModal();

      setTimeout(() => {
        navigate('/dashboard', { replace: true });
        window.location.reload();
      }, MODAL_ANIM_MS);
    } catch {
      setServerError('Что-то пошло не так. Попробуйте еще раз.');
    } finally {
      setLoading(false);
    }
  };

  const pendingIndex = useMemo(
    () => TABS.findIndex((t) => t.id === pendingTab),
    [pendingTab]
  );

  const openModal = () => {
    setIsOpen(true);
    setIsShown(false);
    setActiveTab('login');
    setPendingTab('login');
    setFormData({ name: '', email: '', password: '', confirmPassword: '' });
    setTouchedFields({});
    setServerError('');

    requestAnimationFrame(() => {
      requestAnimationFrame(() => setIsShown(true));
    });
  };

  const closeModal = () => {
    setIsShown(false);
    setTimeout(() => setIsOpen(false), MODAL_ANIM_MS);
  };

  const switchTab = (nextId) => {
    if (nextId === pendingTab) return;
    setPendingTab(nextId);
    setActiveTab(nextId);
    setTouchedFields({});
    setServerError('');
  };

  const wrapRef = useRef(null);
  const contentRef = useRef(null);
  const [bodyH, setBodyH] = useState(0);

  // Добавили isPasswordFocused в зависимости, чтобы форма пересчитывала высоту на мобилках
  useLayoutEffect(() => {
    const content = contentRef.current;
    if (!content || !isOpen) return;

    const update = () => setBodyH(content.scrollHeight);
    update();

    const ro = new ResizeObserver(update);
    ro.observe(content);

    return () => ro.disconnect();
  }, [
    activeTab,
    isOpen,
    formData,
    touchedFields,
    serverError,
    isPasswordFocused,
  ]);

  const strengthLabels = {
    none: 'Введите пароль',
    weak: 'Простой',
    medium: 'Средний',
    strong: 'Сложный',
  };

  return (
    <>
      <button type='button' className={triggerClassName} onClick={openModal}>
        {trigger}
      </button>

      {isOpen &&
        createPortal(
          <div
            className={[
              styles.modalOverlay,
              isShown ? styles.modalOverlayOpen : styles.modalOverlayClose,
            ].join(' ')}
            onClick={closeModal}>
            <div
              className={[
                styles.modal,
                isShown ? styles.modalOpen : styles.modalClose,
              ].join(' ')}
              onClick={(e) => e.stopPropagation()}>
              <div
                ref={wrapRef}
                className={styles.login__inner}
                style={{ height: bodyH ? `${bodyH}px` : undefined }}>
                <div ref={contentRef} className={styles.login__content}>
                  <div className={styles.login__tabs}>
                    <div
                      className={styles.login__tabIndicator}
                      style={{
                        transform: `translateX(${pendingIndex * 100}%)`,
                      }}
                    />
                    {TABS.map((tab) => (
                      <button
                        key={tab.id}
                        type='button'
                        onClick={() => switchTab(tab.id)}
                        disabled={loading}
                        className={[
                          styles.login__tab,
                          pendingTab === tab.id ? styles.login__tabActive : '',
                        ].join(' ')}>
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  <div className={styles.login__header}>
                    <Logo className={styles.login__logo} />
                    <h1 className={styles.login__title}>Добро пожаловать!</h1>
                    <p className={styles.login__subtitle}>
                      {isLogin
                        ? 'Войти в существующий аккаунт'
                        : 'Создать новый аккаунт'}
                    </p>
                  </div>

                  <form
                    onSubmit={handleSubmit}
                    className={
                      isLogin ? styles.login__inputs : styles.reg__inputs
                    }>
                    {!isLogin && (
                      <input
                        name='name'
                        type='text'
                        disabled={loading}
                        className={[
                          styles.login__input,
                          touchedFields.name && !isNameValid
                            ? styles.error
                            : '',
                        ].join(' ')}
                        placeholder='Имя'
                        value={formData.name}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                      />
                    )}

                    <input
                      name='email'
                      type='email'
                      disabled={loading}
                      className={[
                        styles.login__input,
                        touchedFields.email && !isEmailValid
                          ? styles.error
                          : '',
                      ].join(' ')}
                      placeholder='Почта'
                      value={formData.email}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                    />

                    {/* Обертка для пароля и тултипа */}
                    <div className={styles.passwordWrapper}>
                      <input
                        name='password'
                        type='password'
                        disabled={loading}
                        className={[
                          styles.login__input,
                          !isLogin && formData.password
                            ? styles[`input--${passwordStrength}`]
                            : '',
                        ].join(' ')}
                        placeholder='Пароль'
                        value={formData.password}
                        onChange={handleInputChange}
                        onFocus={() => setIsPasswordFocused(true)}
                        onBlur={(e) => {
                          handleBlur(e);
                          setIsPasswordFocused(false);
                        }}
                      />

                      {/* Показываем тултип только при регистрации и фокусе */}
                      {!isLogin && isPasswordFocused && (
                        <div className={styles.passwordTooltip}>
                          <div className={styles.passwordTooltip__list}>
                            {passwordCriteria.map((c) => (
                              <div
                                key={c.id}
                                className={[
                                  styles.passwordTooltip__item,
                                  c.isValid ? styles.valid : styles.invalid,
                                ].join(' ')}>
                                {c.isValid ? (
                                  <svg
                                    viewBox='0 0 24 24'
                                    fill='none'
                                    stroke='currentColor'
                                    strokeWidth='3'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'>
                                    <polyline points='20 6 9 17 4 12' />
                                  </svg>
                                ) : (
                                  <svg
                                    viewBox='0 0 24 24'
                                    fill='none'
                                    stroke='currentColor'
                                    strokeWidth='3'
                                    strokeLinecap='round'
                                    strokeLinejoin='round'>
                                    <line x1='18' y1='6' x2='6' y2='18' />
                                    <line x1='6' y1='6' x2='18' y2='18' />
                                  </svg>
                                )}
                                <span>{c.label}</span>
                              </div>
                            ))}
                          </div>

                          <div
                            className={[
                              styles.passwordTooltip__strength,
                              styles[`strength--${passwordStrength}`],
                            ].join(' ')}>
                            Надежность:{' '}
                            <span>{strengthLabels[passwordStrength]}</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {!isLogin && (
                      <input
                        name='confirmPassword'
                        type='password'
                        disabled={loading}
                        className={[
                          styles.login__input,
                          touchedFields.confirmPassword
                            ? isPassMatch
                              ? styles.success
                              : styles.error
                            : '',
                        ].join(' ')}
                        placeholder='Повторите пароль'
                        value={formData.confirmPassword}
                        onChange={handleInputChange}
                        onBlur={handleBlur}
                      />
                    )}

                    {serverError && (
                      <p className={styles.login__serverError}>{serverError}</p>
                    )}

                    {isLogin && (
                      <p className={styles.login__forgot}>Забыли пароль?</p>
                    )}

                    <button
                      type='submit'
                      disabled={loading || (isLogin ? !canLogin : !canRegister)}
                      className={styles.login__submit}>
                      {loading
                        ? 'Загрузка...'
                        : isLogin
                        ? 'Войти'
                        : 'Зарегистрироваться'}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default Login;
